import { Component, ElementRef, ViewChild, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'app-drawing-canvas',
    templateUrl: './drawing-canvas.component.html',
    styleUrls: ['./drawing-canvas.component.css']
})
export class DrawingCanvasComponent implements AfterViewInit {
    @ViewChild('drawingCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
    @Output() imageReady = new EventEmitter<string>(); // Will emit base64 data URL

    private ctx!: CanvasRenderingContext2D;
    private isDrawing = false;
    selectedColor = '#000000'; // Default color black
    brushSize = 3; // Default line width

    colors = ['#000000', '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffffff']; // Example colors

    constructor(public dialogRef: MatDialogRef<DrawingCanvasComponent>) { }

    ngAfterViewInit(): void {
        const canvas = this.canvasRef.nativeElement;
        // Set initial canvas size to match the container size from CSS
        // We use the offsetWidth/offsetHeight of the canvas element itself
        // which should reflect the size set by CSS.
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        this.ctx = canvas.getContext('2d')!;
        // Check if context was successfully created
        if (!this.ctx) {
            console.error("Failed to get 2D context");
            return;
        }
        this.setupCanvas();
    }

    setupCanvas(): void {
        this.ctx.strokeStyle = this.selectedColor;
        this.ctx.lineWidth = this.brushSize;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        // Add event listeners
        this.canvasRef.nativeElement.addEventListener('mousedown', this.startDrawing.bind(this));
        this.canvasRef.nativeElement.addEventListener('mousemove', this.draw.bind(this));
        this.canvasRef.nativeElement.addEventListener('mouseup', this.stopDrawing.bind(this));
        this.canvasRef.nativeElement.addEventListener('mouseout', this.stopDrawing.bind(this));

        // Touch events
        this.canvasRef.nativeElement.addEventListener('touchstart', this.startDrawing.bind(this), { passive: false });
        this.canvasRef.nativeElement.addEventListener('touchmove', this.draw.bind(this), { passive: false });
        this.canvasRef.nativeElement.addEventListener('touchend', this.stopDrawing.bind(this));
    }

    startDrawing(event: MouseEvent | TouchEvent): void {
        event.preventDefault(); // Prevent scrolling on touch
        this.isDrawing = true;
        const pos = this.getCanvasCoordinates(event);
        this.ctx.beginPath();
        this.ctx.moveTo(pos.x, pos.y);
    }

    draw(event: MouseEvent | TouchEvent): void {
        if (!this.isDrawing) return;
        event.preventDefault(); // Prevent scrolling on touch
        const pos = this.getCanvasCoordinates(event);
        this.ctx.lineTo(pos.x, pos.y);
        this.ctx.stroke();
    }

    stopDrawing(): void {
        if (this.isDrawing) {
            this.ctx.closePath();
            this.isDrawing = false;
        }
    }

    getCanvasCoordinates(event: MouseEvent | TouchEvent): { x: number; y: number } {
        const canvas = this.canvasRef.nativeElement;
        const rect = canvas.getBoundingClientRect();
        let clientX, clientY;

        if (event instanceof MouseEvent) {
            clientX = event.clientX;
            clientY = event.clientY;
        } else { // TouchEvent
            // Ensure there's a touch point
            if (event.touches.length === 0) {
                return { x: 0, y: 0 }; // Or handle as appropriate
            }
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        }

        // Calculate position relative to the canvas element
        const scaleX = canvas.width / rect.width;    // Relationship bitmap vs. element for X
        const scaleY = canvas.height / rect.height;  // Relationship bitmap vs. element for Y

        return {
            x: (clientX - rect.left) * scaleX,   // Scale mouse coordinates after they have
            y: (clientY - rect.top) * scaleY     // been adjusted to be relative to element
        };
    }

    selectColor(color: string): void {
        this.selectedColor = color;
        this.ctx.strokeStyle = this.selectedColor;
    }

    setLineWidth(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.brushSize = parseInt(target.value, 10);
        this.ctx.lineWidth = this.brushSize;
    }

    // New method to handle ngModelChange from slider
    setLineWidthFromModel(newValue: number): void {
        // Check if newValue is a valid number
        if (typeof newValue === 'number' && !isNaN(newValue)) {
            this.brushSize = newValue;
            if (this.ctx) { // Ensure context exists
                this.ctx.lineWidth = this.brushSize;
            }
        } else {
            console.error("Invalid value received for line width:", newValue);
        }
    }

    clearCanvas(): void {
        const canvas = this.canvasRef.nativeElement;
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    done(): void {
        const canvas = this.canvasRef.nativeElement;
        const dataUrl = canvas.toDataURL('image/png'); // Get image as PNG data URL
        this.dialogRef.close(dataUrl); // Close dialog and pass data back
    }

    closeDialog(): void {
        this.dialogRef.close(); // Close without saving
    }
} 